import { useEffect, useState, useRef, useCallback } from 'react';
import { resendVerificationEmail } from '../api/resendVerificationEmail';

interface EmailNotVerifiedModalProps {
  email: string;
  onClose: () => void;
}

type State =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; expiresAt: number }
  | { kind: 'rate_limited'; expiresAt: number }
  | { kind: 'error'; message: string };

function secondsLeft(expiresAt: number): number {
  return Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
}

export function EmailNotVerifiedModal({ email, onClose }: EmailNotVerifiedModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [tick, setTick] = useState(0); // forces re-render every 1s for countdowns

  // Tick interval — only runs while there's a countdown to display.
  useEffect(() => {
    if (state.kind !== 'sent' && state.kind !== 'rate_limited') return;
    const handle = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(handle);
  }, [state.kind]);

  // Auto-return to idle when the countdown hits 0.
  useEffect(() => {
    if (state.kind !== 'sent' && state.kind !== 'rate_limited') return;
    if (secondsLeft(state.expiresAt) <= 0) setState({ kind: 'idle' });
  }, [state, tick]);

  // Esc to close.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Focus the dialog on mount for screen readers.
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const handleResend = useCallback(async () => {
    setState({ kind: 'sending' });
    const result = await resendVerificationEmail();
    if (result.ok) {
      setState({ kind: 'sent', expiresAt: Date.now() + 60_000 });
      return;
    }
    if (result.code === 'RATE_LIMITED') {
      const details = (result as unknown as { details?: { retryAfterSeconds?: number } }).details;
      const seconds = details?.retryAfterSeconds ?? 60;
      setState({ kind: 'rate_limited', expiresAt: Date.now() + seconds * 1000 });
      return;
    }
    if (result.code === 'ALREADY_VERIFIED') {
      setState({ kind: 'error', message: 'Looks like your email is already verified. Refresh the page.' });
      return;
    }
    setState({ kind: 'error', message: "Couldn't send. Try again in a moment." });
  }, []);

  const isBusy = state.kind === 'sending' || state.kind === 'sent' || state.kind === 'rate_limited';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="email-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
      >
        <h2 id="email-modal-title" className="text-lg font-semibold text-ink mb-2">
          Verify your email to invite members
        </h2>
        <p className="text-sm text-ink-muted mb-4">
          We sent a verification link to <strong>{email}</strong> when you signed up.
          Click the link in that email, then try inviting again. Didn&apos;t receive it?
          Check spam or resend.
        </p>

        {state.kind === 'rate_limited' && (
          <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
            Just sent one — please wait {secondsLeft(state.expiresAt)}s before resending.
          </div>
        )}
        {state.kind === 'error' && (
          <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded text-xs text-red-900">
            {state.message}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            id="email-not-verified-cancel-button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-ink rounded-lg hover:bg-surface-hover"
          >
            Cancel
          </button>
          {state.kind === 'sent' ? (
            <button
              id="email-resend-sent-button"
              type="button"
              disabled
              className="px-4 py-2 text-sm bg-green-100 text-green-900 rounded-lg cursor-default"
            >
              Sent ✓ Check your inbox ({secondsLeft(state.expiresAt)}s)
            </button>
          ) : (
            <button
              type="button"
            id="email-resend-button"
            onClick={handleResend}
              disabled={isBusy}
              className="px-4 py-2 text-sm font-medium bg-hbs-primary text-hbs-on-primary rounded-lg hover:bg-hbs-primary-hover disabled:opacity-50"
            >
              {state.kind === 'sending' ? 'Sending…' : 'Resend email'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
